import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Users, FileText, AlignLeft, Shield } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Input from '../../common/Input';
import { createGroup } from '../../../services/groupServiceSwitch';
import { useTeam } from '../../../context/TeamContext';
import './GroupManagement.css';

/**
 * CreateGroupModal Component
 * 
 * Modal for creating new distribution groups
 */
export default function CreateGroupModal({ isOpen, onClose, onSuccess }) {
  const { authorisedTeams, activeTeamId } = useTeam();
  const realTeams = authorisedTeams.filter(t => t.id !== null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    teamId: activeTeamId || ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Default the team to the active team each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, teamId: activeTeamId || '' }));
    }
  }, [isOpen, activeTeamId]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Team validation
    if (!formData.teamId) {
      newErrors.teamId = 'Select a team';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Group name must be at least 3 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Call API to create group
      await createGroup(formData);

      // Show success notification
      if (onSuccess) {
        onSuccess('Group created successfully');
      }

      // Reset form and close modal
      setFormData({ name: '', description: '', teamId: activeTeamId || '' });
      setErrors({});
      onClose();
    } catch (error) {
      setErrors({
        submit: error.message || 'Failed to create group'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', description: '', teamId: activeTeamId || '' });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Group"
      size="md"
      closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            icon={<Users size={18} />}
          >
            Create Group
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="group-form">
        {/* Team Selector */}
        {realTeams.length > 1 && (
          <div className="form-group">
            <label htmlFor="teamId" className="form-label">
              <Shield size={16} />
              Team
            </label>
            <select id="teamId" name="teamId" value={formData.teamId} onChange={handleChange} className={`form-select ${errors.teamId ? 'error' : ''}`} disabled={loading}>
              <option value="">Select a team</option>
              {realTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {errors.teamId && <span className="error-text">{errors.teamId}</span>}
          </div>
        )}

        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            <FileText size={16} />
            Group Name
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            placeholder="Sales Team Distribution"
            error={errors.name}
            disabled={loading}
          />
          {errors.name && (
            <span className="error-text">{errors.name}</span>
          )}
        </div>

        {/* Description Field */}
        <div className="form-group">
          <label htmlFor="description" className="form-label">
            <AlignLeft size={16} />
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe the purpose of this distribution group..."
            className={`form-textarea ${errors.description ? 'error' : ''}`}
            disabled={loading}
            rows={4}
          />
          {errors.description && (
            <span className="error-text">{errors.description}</span>
          )}
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="form-error">
            {errors.submit}
          </div>
        )}
      </form>
    </Modal>
  );
}

CreateGroupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};
